import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UtilitiesService } from 'src/app/core/utilities.service';

export type MsgBoxIcon =
  | 'error'
  | 'warning'
  | 'info'
  | 'none'
  | 'success'
  | 'question';

export type MsgBoxButtonStyle =
  | 'danger'
  | 'warning'
  | 'info'
  | 'none'
  | 'success'
  | 'primary'
  | 'secondary'
  | 'danger-inverse'
  | 'warning-inverse'
  | 'info-inverse'
  | 'none-inverse'
  | 'success-inverse'
  | 'primary-inverse'
  | 'secondary-inverse';

export type MsgBoxButton = {
  type?: string;
  classList?: string | string[];
  style?: string;
  text?: string;
  iconClass?: string | string[];
  onClick?: 'close' | Function;
  onMouseOver?: Function;
  onMouseOut?: Function;
};

export type MsgBoxOptions = {
  message?: string;
  icon?: MsgBoxIcon;
  title?: string;
  buttonList?: MsgBoxButton[];
  contentClassList?:any;
};

@Component({
  selector: 'app-msgbox',
  templateUrl: './msgbox.component.html',
  styleUrls: ['./msgbox.component.scss'],
  providers: [NgbModal, NgbActiveModal],
})
export class MsgBoxComponent implements OnInit {
  @ViewChild('contentTemplate') contentTemplate: TemplateRef<any>;

  constructor(
    public utils: UtilitiesService,
    public modalService: NgbModal,
    public activeModal: NgbActiveModal
  ) {}
  icon: MsgBoxIcon = 'none';
  title = '';
  message = '';
  buttonList: any[] = [];
  contentClassList: any = [];

  private readonly defaultButtonList: any[] = [
    { type: 'spacing' },
    { text: 'OK', style: 'success', onClick: 'close' },
    { type: 'spacing' },
  ];

  private currentModalRef: any = null;

  public open(messageOrOptions?: string | MsgBoxOptions) {
    let options: MsgBoxOptions =
      typeof messageOrOptions === 'string'
        ? { message: messageOrOptions }
        : messageOrOptions;

    this.message = options.message ?? '';
    this.icon = options.icon ?? 'none';
    this.title = options.title ?? '';
    this.buttonList = options.buttonList ?? this.defaultButtonList;
    this.contentClassList = options.contentClassList ?? [];

    // One at a time. currentModalRef only ever remembered the newest, so a
    // second open() stacked another modal that nothing could then close —
    // dismissing the top one just revealed the one behind it.
    this.closeModal();

    this.currentModalRef = this.modalService.open(this.contentTemplate, {
      windowClass: 'backdrop-blur-sm transition !duration-0',
      ariaLabelledBy: 'modal-basic-title',
      backdropClass: 'bg-slate-700',
      centered: true,
    });
  }

  closeModal() {
    this.currentModalRef?.close();
    this.currentModalRef = null;
  }
  onClick(button, event) {
    if (typeof button?.onClick === 'function') button?.onClick(this, event);
    if (typeof button?.onClick === 'string') {
      if (button?.onClick === 'close') {
        this.closeModal();
      }
    }
    this.closeModal();
  }
  onMouseOver(button, event) {
    if (typeof button?.onMouseOver === 'function')
      button?.onMouseOver(this, event);
  }
  onMouseOut(button, event) {
    if (typeof button?.onMouseOut === 'function')
      button?.onMouseOut(this, event);
  }

  getIconClass(icon: MsgBoxIcon) {
    if (icon === 'error') return `text-rose-600  fi fi-rr-cross-circle mt-1`;
    if (icon === 'info') return `text-cyan-600  fi fi-rr-info mt-1`;
    if (icon === 'question')
      return `text-cyan-600  fi fi-rr-question-square mt-1`;
    if (icon === 'success') return `text-green-600  fi fi-rr-check mt-1`;
    if (icon === 'warning') return `text-amber-600  fi fi-rr-exclamation mt-1`;
    return ``;
  }

  getButtonStyle(style: MsgBoxButtonStyle) {
    if (style === 'danger-inverse')
      return `bg-white border !border-red-500 text-red-500 duration-300 transition ease-in-out hover:text-white hover:!bg-red-500 `;
    if (style === 'info-inverse')
      return `bg-white border !border-cyan-500 text-cyan-500 duration-300 transition ease-in-out hover:text-white hover:!bg-cyan-500/[0.9] `;
    if (style === 'success-inverse')
      return `bg-white border !border-green-500 text-green-500 duration-300 transition ease-in-out hover:text-white hover:!bg-green-500/[0.9] `;
    if (style === 'warning-inverse')
      return `bg-white border !border-amber-500 text-amber-500 duration-300 transition ease-in-out hover:text-white hover:!bg-amber-500/[0.9] `;
    if (style === 'primary-inverse')
      return `bg-white border !border-indigo-500 text-indigo-500 duration-300 transition ease-in-out hover:text-white hover:!bg-indigo-500 `;
    if (style === 'secondary-inverse')
      return `bg-white border !border-slate-400 text-slate-400 duration-300 transition ease-in-out hover:text-white hover:!bg-slate-400/[0.8] `;

    if (style === 'danger')
      return `border !border-red-500 bg-red-500 hover:bg-red-600 hover:!border-red-600 text-white duration-300 transition ease-in-out`;
    if (style === 'info')
      return `border !border-cyan-500 bg-cyan-500 hover:bg-cyan-600/[0.9] hover:!border-cyan-600/[0.9] text-white duration-300 transition ease-in-out`;
    if (style === 'success')
      return `border !border-green-500 bg-green-500 hover:bg-green-600/[0.9] hover:!border-green-600/[0.9] text-white duration-300 transition ease-in-out`;
    if (style === 'warning')
      return `border !border-amber-500 bg-amber-500 hover:bg-amber-600/[0.9] hover:!border-amber-600/[0.9] text-white duration-300 transition ease-in-out`;
    if (style === 'primary')
      return `border !border-indigo-500 bg-indigo-500 hover:bg-indigo-600 hover:!border-indigo-600 text-white duration-300 transition ease-in-out`;
    if (style === 'secondary')
      return `border !border-slate-400 bg-slate-400 hover:bg-slate-500/[0.8] hover:!border-slate-500/[0.8] text-white duration-300 transition ease-in-out`;

    return `border !border-gray-100 bg-gray-100 hover:bg-gray-200 text hover:bg-gray-200  hover:!border-gray-200 text-gray-800 duration-300 transition ease-in-out`;
  }

  getButtonClass(classList?: string | string[]) {
    if (typeof classList === 'undefined') classList = [];
    if (typeof classList === 'string') classList = [classList];
    return classList.join(' ');
  }

  getContentClass(classList?: string | string[]) {
    if (typeof classList === 'undefined') classList = [];
    if (typeof classList === 'string') classList = [classList];
    return classList.join(' ');
  }

  ngOnInit() {
    this.utils.msgBox = this;
  }

  ngOnDestroy() {}
}
